import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import HeroSection from "../components/HeroSection";
import { get } from "../api/config";

const enhanceDescription = (description, projectName) => {
  if (!description) return "A well-executed infrastructure project that has successfully improved community facilities.";
  
  // If description is already good, return as is
  if (description.length > 100) return description;
  
  // Enhance short descriptions based on project type
  const name = projectName?.toLowerCase() || "";
  
  if (name.includes("stadium") || name.includes("ground") || name.includes("khel") || name.includes("sports")) {
    return `${description} This world-class sports facility has been developed to provide excellent training and competitive opportunities for athletes of all levels.`;
  } else if (name.includes("road")) {
    return `${description} This infrastructure project has significantly improved connectivity and transportation efficiency in the region.`;
  } else if (name.includes("drain") || name.includes("water")) {
    return `${description} This drainage and water management system has enhanced sanitation and reduced waterlogging issues in the area.`;
  } else if (name.includes("school") || name.includes("education")) {
    return `${description} This educational facility has been constructed to provide quality learning infrastructure for students.`;
  } else if (name.includes("health") || name.includes("hospital")) {
    return `${description} This health facility has been established to provide accessible healthcare services to the community.`;
  }
  
  return `${description} This project represents a significant contribution to community development and infrastructure improvement.`;
};

const ProjectDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [activeGalleryTab, setActiveGalleryTab] = useState("all");
  const [galleryView, setGalleryView] = useState("grid");

  useEffect(() => {
    if (id) {
      fetchProjectDetails();
    }
  }, [id]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsAnimating(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const fetchProjectDetails = async () => {
    try {
      setLoading(true);
      const response = await get(`public/projects/${id}`);
      setProjectData(response.data);
    } catch (err) {
      setError("Failed to load project details");
      console.error("Error fetching project details:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return "₹ 0";
    const numAmount = parseFloat(amount);

    if (numAmount >= 10000000) {
      return `₹ ${(numAmount / 10000000).toFixed(1)} Cr`;
    } else if (numAmount >= 100000) {
      return `₹ ${(numAmount / 100000).toFixed(1)} L`;
    }

    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).replace(/\//g, '/');
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return getDefaultProjectImage();
    if (imagePath.startsWith("http")) return imagePath;
    const cleanPath = imagePath.startsWith("/") ? imagePath : `/${imagePath}`;
    return `https://dsdspl.com/mp_funds/public${cleanPath}`;
  };

  const getDocumentUrl = (docPath) => {
    if (!docPath) return null;
    if (docPath.startsWith("http")) return docPath;
    const cleanPath = docPath.startsWith("/") ? docPath : `/${docPath}`;
    return `https://dsdspl.com/mp_funds/public${cleanPath}`;
  };

  const getDefaultProjectImage = () => {
    const projectName = projectData?.project_name?.toLowerCase() || "";
    if (projectName.includes("drain")) {
      return "https://images.unsplash.com/photo-1581094794329-c8112c4e5190?w=600&h=400&fit=crop";
    } else if (projectName.includes("road")) {
      return "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=600&h=400&fit=crop";
    } else {
      return "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&h=400&fit=crop";
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "from-green-500 to-green-600";
      case "ongoing":
        return "from-blue-500 to-blue-600";
      case "proposed":
        return "from-yellow-500 to-yellow-600";
      default:
        return "from-gray-500 to-gray-600";
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "✅";
      case "ongoing":
        return "🔄";
      case "proposed":
        return "📋";
      default:
        return "❓";
    }
  };

  // Get all images including project images and timeline images
  const getAllImages = () => {
    const projectImages = (projectData?.images || []).map(img => ({
      ...img,
      type: "project",
      timeline_date: null,
      timeline_text: null
    }));

    const timelineImages = (projectData?.timeline || []).flatMap(timeline => 
      (timeline.images || []).map(img => ({
        ...img,
        type: "timeline",
        timeline_date: timeline.timeline_date,
        timeline_text: timeline.text,
        timeline_id: timeline.timeline_id
      }))
    );

    return [...projectImages, ...timelineImages];
  };

  // Get images filtered by gallery tab
  const getFilteredImages = () => {
    const allImages = getAllImages();
    
    switch (activeGalleryTab) {
      case "project":
        return allImages.filter(img => img.type === "project");
      case "timeline":
        return allImages.filter(img => img.type === "timeline");
      default:
        return allImages;
    }
  };

  // Group timeline images by date
  const getTimelineGroups = () => {
    const timelineGroups = {};
    
    (projectData?.timeline || []).forEach(timeline => {
      if (timeline.images && timeline.images.length > 0) {
        timelineGroups[timeline.timeline_date] = {
          date: timeline.timeline_date,
          text: timeline.text,
          images: timeline.images.map(img => ({
            ...img,
            type: "timeline",
            timeline_date: timeline.timeline_date,
            timeline_text: timeline.text
          }))
        };
      }
    });

    return Object.values(timelineGroups);
  };

  // Open image in modal with navigation
  const openImage = (image, index) => {
    setSelectedImage(getImageUrl(image.image_path || image.image_url));
    setCurrentImageIndex(index);
  };

  // Navigate to next image
  const nextImage = () => {
    const filteredImages = getFilteredImages();
    const nextIndex = (currentImageIndex + 1) % filteredImages.length;
    setCurrentImageIndex(nextIndex);
    setSelectedImage(getImageUrl(filteredImages[nextIndex].image_path || filteredImages[nextIndex].image_url));
  };

  // Navigate to previous image
  const prevImage = () => {
    const filteredImages = getFilteredImages();
    const prevIndex = (currentImageIndex - 1 + filteredImages.length) % filteredImages.length;
    setCurrentImageIndex(prevIndex);
    setSelectedImage(getImageUrl(filteredImages[prevIndex].image_path || filteredImages[prevIndex].image_url));
  };

  // Check if file is video
  const isVideoFile = (filename) => {
    if (!filename) return false;
    const videoExtensions = ['.mp4', '.mov', '.avi', '.mkv', '.webm'];
    return videoExtensions.some(ext => filename.toLowerCase().includes(ext));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <HeroSection />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600 mb-4"></div>
            <div className="text-gray-600 text-lg font-medium">Loading project details...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !projectData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
        <HeroSection />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center py-20">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <div className="text-red-600 text-xl mb-6 font-medium">
              {error || "Project not found"}
            </div>
            <button
              onClick={() => navigate(-1)}
              className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-3 rounded-xl hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow-lg hover:shadow-xl font-medium"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const allImages = getAllImages();
  const filteredImages = getFilteredImages();
  const timelineGroups = getTimelineGroups();
  const progress = projectData.progress ||
    (projectData.status?.toLowerCase() === "completed" ? "100" : "0");

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Hero Section */}
      <div className="relative">
        <HeroSection />
      </div>

      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 py-6 mt-4">
        <button
          onClick={() => navigate(-1)}
          className="group bg-white/80 backdrop-blur-sm text-green-600 hover:text-green-700 font-medium flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-white transition-all duration-300 shadow-sm hover:shadow-md border border-green-100"
        >
          <span className="group-hover:-translate-x-1 transition-transform duration-300">←</span>
          Back to Projects
        </button>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 pb-16">
        {/* Title Section */}
        <div className="text-center mb-12">
          <div className="inline-block relative">
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent inline-block">
              Project Details
            </h1>
            <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-green-500 to-blue-500 rounded-full"></div>
          </div>
        </div>

        {/* Project Details Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {/* Left Column - Image and Basic Info */}
          <div className="lg:col-span-1 space-y-6">
            {/* Project Image Card */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <div className="relative group">
                <img
                  src={getImageUrl(projectData.images?.[0]?.image_url)}
                  alt={projectData.project_name}
                  className="w-full h-64 object-cover rounded-xl mb-4 shadow-md group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-3">
                {projectData.project_name}
              </h2>
              
              {/* Status Badge */}
              <div className="mb-4">
                <div
                  className={`text-white py-3 px-6 rounded-xl text-center font-bold bg-gradient-to-r ${getStatusColor(
                    projectData.status
                  )} shadow-md`}
                >
                  <span className="mr-2">{getStatusIcon(projectData.status)}</span>
                  {projectData.status || "Unknown Status"}
                </div>
              </div>
            </div>

            {/* Progress Section */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <span className="text-green-600">📊</span>
                Project Progress
              </h3>

              {/* Progress Bar */}
              <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm font-medium text-gray-700">
                    Completion Status
                  </span>
                  <span className="text-sm font-bold text-green-600 bg-green-50 px-3 py-1 rounded-full">
                    {progress}%
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-4 relative overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-green-500 to-green-600 h-4 rounded-full transition-all duration-1000 ease-out shadow-inner relative"
                    style={{
                      width: `${progress}%`,
                    }}
                  >
                    {/* Animated Runner */}
                    <div
                      className="absolute right-0 top-1/2 transform -translate-y-1/2 text-lg transition-all duration-300"
                      style={{
                        marginRight: "-8px",
                        animation: isAnimating ? "run 0.5s infinite alternate" : "none",
                      }}
                    >
                      🏃‍♂️‍➡️
                    </div>
                  </div>
                </div>
              </div>

              {/* Project Length */}
              {projectData.length_size && (
                <div className="flex justify-between items-center py-3 border-t border-gray-100">
                  <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <span>📏</span>
                    Project Length
                  </span>
                  <span className="text-sm font-bold text-gray-900 bg-gray-50 px-3 py-1 rounded-full">
                    {projectData.length_size}
                  </span>
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="bg-gradient-to-br from-green-500 to-blue-500 rounded-2xl p-6 text-white shadow-lg">
              <h3 className="text-xl font-bold mb-4">Quick Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span>Start Date</span>
                  <span className="font-semibold">{formatDate(projectData.start_date)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>End Date</span>
                  <span className="font-semibold">{formatDate(projectData.end_date)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Budget</span>
                  <span className="font-semibold">{formatCurrency(projectData.estimated_cost)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Project Description */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <div className="w-2 h-8 bg-gradient-to-b from-green-500 to-blue-500 rounded-full"></div>
                Project Description
              </h3>
              <p className="text-gray-700 leading-relaxed text-lg">
                {enhanceDescription(projectData.description, projectData.project_name)}
              </p>
            </div>

            {/* Key Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Fund Allocated */}
              <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-6 shadow-lg border border-green-100">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center shadow-md">
                    <span className="text-white text-xl">💰</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Fund Allocated</h3>
                  </div>
                </div>
                <p className="text-3xl font-bold text-green-600">
                  {formatCurrency(projectData.estimated_cost)}
                </p>
              </div>

              {/* Location */}
              <div className="bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl p-6 shadow-lg border border-blue-100">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center shadow-md">
                    <span className="text-white text-xl">📍</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Location</h3>
                  </div>
                </div>
                <p className="text-gray-700 text-lg font-medium">{projectData.location}</p>
              </div>
            </div>

            {/* Politician Info */}
            {projectData.politician && (
              <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                  <span className="text-2xl">👨‍💼</span>
                  Leader
                </h3>
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-lg">
                    <span className="text-white font-bold text-2xl">
                      {projectData.politician.full_name?.charAt(0) || "P"}
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-gray-900 text-xl">
                      {projectData.politician.full_name}
                    </p>
                    {projectData.politician.designation && (
                      <p className="text-gray-600 text-lg mt-1">
                        {projectData.politician.designation}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Project Summary */}
        {projectData.summary && projectData.summary.length > 0 && (
          <div className="mb-16">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent inline-block">
                Project Highlights
              </h2>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {projectData.summary.map((item, index) => (
                  <div
                    key={item.id || index}
                    className="flex items-start gap-4 p-4 rounded-xl bg-gradient-to-r from-gray-50 to-white hover:from-green-50 hover:to-blue-50 transition-all duration-300 group"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-blue-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm group-hover:shadow-md transition-shadow duration-300">
                      <span className="text-white font-bold text-sm">✓</span>
                    </div>
                    <span className="text-gray-700 text-lg leading-relaxed group-hover:text-gray-900 transition-colors duration-300">
                      {item.points}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Project Timeline */}
        {projectData.timeline && projectData.timeline.length > 0 && (
          <div className="mb-16">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent inline-block">
                Project Timeline
              </h2>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
              <div className="relative max-w-4xl mx-auto">
                <div className="absolute left-1/2 top-0 w-1 h-full bg-gradient-to-b from-green-500 to-blue-500 transform -translate-x-1/2 rounded-full shadow-lg"></div>
                {projectData.timeline.map((item, index) => {
                  const isRight = index % 2 === 0;
                  const hasImages = item.images && item.images.length > 0;
                  
                  return (
                    <div
                      key={item.timeline_id || index}
                      className={`flex justify-center items-start mb-12 last:mb-0 ${
                        isRight ? "" : "flex-row-reverse"
                      }`}
                    >
                      <div className={`w-5/12 ${isRight ? "pr-8" : "pl-8"}`}>
                        <div className="bg-gradient-to-br from-gray-50 to-white p-6 rounded-2xl shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 group">
                          <div className="flex items-center justify-between mb-3">
                            <div className="text-lg font-bold text-green-600 flex items-center gap-2">
                              <span>📅</span>
                              {formatDate(item.timeline_date)}
                            </div>
                            {hasImages && (
                              <button
                                onClick={() => {
                                  setActiveGalleryTab("timeline");
                                  setGalleryView("timeline");
                                  // Scroll to gallery section
                                  document.getElementById('gallery-section')?.scrollIntoView({ 
                                    behavior: 'smooth' 
                                  });
                                }}
                                className="flex items-center gap-2 px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200 text-sm"
                              >
                                <span>🖼️</span>
                                {item.images.length} {item.images.length === 1 ? 'Image' : 'Images'}
                              </button>
                            )}
                          </div>
                          <div className="text-gray-900 font-medium text-lg mb-3 leading-relaxed">
                            {item.text}
                          </div>
                          {item.document && (
                            <div className="text-sm">
                              <a
                                href={getDocumentUrl(item.document)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-2 group-hover:underline transition-all duration-300"
                              >
                                <span className="text-lg">📄</span>
                                {item.document.split("/").pop()}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="w-2/12 flex justify-center">
                        <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-blue-500 rounded-full relative flex items-center justify-center shadow-lg border-2 border-white">
                          <div className="w-2 h-2 bg-white rounded-full"></div>
                        </div>
                      </div>
                      <div className="w-5/12"></div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Project Gallery */}
        {allImages.length > 0 && (
          <div id="gallery-section" className="mb-12">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent inline-block mb-4">
                Project Gallery
              </h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Explore all project images and timeline updates in one place
              </p>
            </div>

            {/* Gallery Controls */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 mb-8">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                {/* Tabs */}
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: "all", label: "All Images", count: allImages.length, icon: "🖼️" },
                    { key: "project", label: "Project", count: allImages.filter(img => img.type === "project").length, icon: "🏗️" },
                    { key: "timeline", label: "Timeline", count: allImages.filter(img => img.type === "timeline").length, icon: "📅" }
                  ].map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveGalleryTab(tab.key)}
                      className={`flex items-center gap-2 px-4 py-3 rounded-xl font-medium transition-all duration-300 ${
                        activeGalleryTab === tab.key
                          ? "bg-gradient-to-r from-green-500 to-blue-500 text-white shadow-lg"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      <span className="text-lg">{tab.icon}</span>
                      {tab.label}
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        activeGalleryTab === tab.key
                          ? "bg-white/20 text-white"
                          : "bg-gray-300 text-gray-700"
                      }`}>
                        {tab.count}
                      </span>
                    </button>
                  ))}
                </div>

                {/* View Toggle */}
                <div className="flex gap-2 bg-gray-100 p-1 rounded-xl">
                  <button
                    onClick={() => setGalleryView("grid")}
                    className={`p-2 rounded-lg transition-all duration-300 ${
                      galleryView === "grid"
                        ? "bg-white text-green-600 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    <span className="text-lg">🔲</span>
                  </button>
                  <button
                    onClick={() => setGalleryView("timeline")}
                    className={`p-2 rounded-lg transition-all duration-300 ${
                      galleryView === "timeline"
                        ? "bg-white text-green-600 shadow-sm"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    <span className="text-lg">📊</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Gallery Content */}
            {galleryView === "grid" ? (
              // Grid View
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredImages.map((image, index) => (
                  <div
                    key={image.id || image.image_id || index}
                    className="group relative aspect-square overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer bg-white"
                    onClick={() => openImage(image, index)}
                  >
                    {isVideoFile(image.image_path || image.image_url) ? (
                      <video
                        src={getImageUrl(image.image_path || image.image_url)}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        muted
                        loop
                        playsInline
                      />
                    ) : (
                      <img
                        src={getImageUrl(image.image_path || image.image_url)}
                        alt={`Gallery ${index + 1}`}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4">
                      <div className="text-white">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm bg-white/20 px-2 py-1 rounded-full">
                            {image.type === "timeline" ? "📅 Timeline" : "🏗️ Project"}
                          </span>
                          {isVideoFile(image.image_path || image.image_url) && (
                            <span className="text-sm bg-red-500/80 px-2 py-1 rounded-full">🎥 Video</span>
                          )}
                        </div>
                        {image.timeline_date && (
                          <p className="text-xs text-white/90 mb-1">
                            {formatDate(image.timeline_date)}
                          </p>
                        )}
                        {image.timeline_text && (
                          <p className="text-xs text-white/80 line-clamp-2">
                            {image.timeline_text}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Image Type Badge */}
                    <div className="absolute top-3 left-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
                      {isVideoFile(image.image_path || image.image_url) ? "🎥" : 
                       image.type === "timeline" ? "📅" : "🏗️"}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Timeline View
              <div className="space-y-8">
                {timelineGroups.map((group, index) => (
                  <div key={group.date} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-lg">
                        <span className="text-white font-bold text-xl">📅</span>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">
                          {formatDate(group.date)}
                        </h3>
                        <p className="text-gray-600 mt-1">{group.text}</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {group.images.map((image, imgIndex) => (
                        <div
                          key={image.id}
                          className="group relative aspect-square overflow-hidden rounded-xl shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer"
                          onClick={() => {
                            const allTimelineImages = timelineGroups.flatMap(g => g.images);
                            const globalIndex = allTimelineImages.findIndex(img => img.id === image.id);
                            openImage(image, globalIndex);
                          }}
                        >
                          {isVideoFile(image.image_path) ? (
                            <video
                              src={getImageUrl(image.image_path)}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                              muted
                              loop
                              playsInline
                            />
                          ) : (
                            <img
                              src={getImageUrl(image.image_path)}
                              alt={`Timeline image ${imgIndex + 1}`}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            />
                          )}
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                            <span className="text-white text-lg font-medium">👁️ View</span>
                          </div>
                          {isVideoFile(image.image_path) && (
                            <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                              🎥
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                
                {/* Project Images Section in Timeline View */}
                {activeGalleryTab !== "timeline" && allImages.filter(img => img.type === "project").length > 0 && (
                  <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg">
                        <span className="text-white font-bold text-xl">🏗️</span>
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">
                          Project Images
                        </h3>
                        <p className="text-gray-600 mt-1">General project photographs and documentation</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {allImages
                        .filter(img => img.type === "project")
                        .map((image, index) => (
                          <div
                            key={image.image_id}
                            className="group relative aspect-square overflow-hidden rounded-xl shadow-md hover:shadow-lg transition-all duration-300 cursor-pointer"
                            onClick={() => {
                              const projectImages = allImages.filter(img => img.type === "project");
                              const globalIndex = projectImages.findIndex(img => img.image_id === image.image_id);
                              openImage(image, globalIndex);
                            }}
                          >
                            {isVideoFile(image.image_url) ? (
                              <video
                                src={getImageUrl(image.image_url)}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                muted
                                loop
                                playsInline
                              />
                            ) : (
                              <img
                                src={getImageUrl(image.image_url)}
                                alt={`Project image ${index + 1}`}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                              />
                            )}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                              <span className="text-white text-lg font-medium">👁️ View</span>
                            </div>
                            {isVideoFile(image.image_url) && (
                              <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                                🎥
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Empty State */}
            {filteredImages.length === 0 && (
              <div className="text-center py-16 bg-white rounded-2xl shadow-lg border border-gray-100">
                <div className="text-6xl mb-4">📷</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">No images found</h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  {activeGalleryTab === "project" 
                    ? "No project images available for this category."
                    : "No timeline images available for this category."}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Enhanced Image Modal with Navigation */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div className="relative max-w-6xl max-h-full w-full">
            {/* Navigation Arrows */}
            {filteredImages.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    prevImage();
                  }}
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-2xl hover:scale-110 transition-all duration-200 z-10 backdrop-blur-sm"
                >
                  <span className="text-2xl font-bold">←</span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    nextImage();
                  }}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/20 hover:bg-white/30 text-white rounded-full w-12 h-12 flex items-center justify-center shadow-2xl hover:scale-110 transition-all duration-200 z-10 backdrop-blur-sm"
                >
                  <span className="text-2xl font-bold">→</span>
                </button>
              </>
            )}

            {/* Image Counter */}
            {filteredImages.length > 1 && (
              <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-full text-sm backdrop-blur-sm">
                {currentImageIndex + 1} / {filteredImages.length}
              </div>
            )}

            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-2" onClick={(e) => e.stopPropagation()}>
              {isVideoFile(selectedImage) ? (
                <video
                  src={selectedImage}
                  className="max-w-full max-h-[80vh] object-contain rounded-lg mx-auto"
                  controls
                  autoPlay
                  muted
                  loop
                />
              ) : (
                <img
                  src={selectedImage}
                  alt="Enlarged view"
                  className="max-w-full max-h-[80vh] object-contain rounded-lg mx-auto"
                />
              )}
            </div>
            <button
              className="absolute -top-4 -right-4 bg-red-500 hover:bg-red-600 rounded-full w-12 h-12 flex items-center justify-center shadow-2xl hover:scale-110 transition-all duration-200 text-white font-bold text-xl"
              onClick={() => setSelectedImage(null)}
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Add CSS animation for the runner */}
      <style jsx>{`
        @keyframes run {
          0% {
            transform: translateY(-50%) translateX(0px);
          }
          100% {
            transform: translateY(-50%) translateX(-4px);
          }
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default ProjectDetailsPage;